<!-- Product states (PD §5): 3 skeleton cards (P/proto-states.js:130), the error panel, the gone-listing panel (:121). -->
@if (loading) {
    <agu-skeleton [Count]="3" />
} @else if (error) {
    <agu-state-panel
        Variant="error"
        [Icon]="ErrorCopy.Icon"
        [Title]="ErrorCopy.Title"
        [Body]="ErrorCopy.Body"
        PrimaryLabel="Try again"
        [SecondaryLabel]="failures.SupportHref ? 'Contact support' : null"
        [SecondaryHref]="failures.SupportHref"
        [Foot]="failures.Foot"
        [Note]="failures.Note"
        (Primary)="Retry()"
    />
} @else if (!profile) {
    <agu-state-panel [Icon]="GoneCopy.Icon" [Title]="GoneCopy.Title" [Body]="GoneCopy.Body" PrimaryLabel="See similar" SecondaryLabel="Back to Home" (Primary)="SeeSimilar.emit()" (Secondary)="BackToHome.emit()" />
} @else {
    <!-- The crumb trail (P/detail.html:888): "Back to {origin}" / this record. Hidden on a phone, where the app bar carries it (P/Member_Hub.html:493). -->
    <agu-crumb [Trail]="Crumb.Trail" (Navigate)="Crumb.Navigate($event)" />

    <!-- detail.html's plain document head (R-F11): pills, 30px h1, 14.5px meta, on the page ground. -->
    @if (IsRecommended || IsNew) {
        <div class="ra-dv__pills">
            @if (IsRecommended) {
                <span class="ra-dv__rectag">
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" class="ra-dv__rectag-star">
                        <path d="M12 2l2.4 6.6L21 11l-6.6 2.4L12 20l-2.4-6.6L3 11l6.6-2.4z" />
                    </svg>
                    Recommended
                </span>
            }
            @if (IsNew) {
                <span class="ra-dv__newpill">New</span>
            }
        </div>
    }

    <h1 class="ra-dv__title">{{ profile.name }}</h1>
    @if (MetaLine) {
        <p class="ra-dv__meta">{{ MetaLine }}</p>
    }

    <!-- detail.html:377 gives the expert three `.fact` tiles; the fourth (members connected) is a profile stat we have, in the g4 grid the other views use. -->
    <div class="ra-dv__facts ra-dv__facts--g4">
        <div class="ra-dv__fact">
            <div class="ra-dv__fact-k">Works on record</div>
            <div class="ra-dv__fact-v">{{ profile.stats.worksOnRecord }}</div>
        </div>
        <div class="ra-dv__fact">
            <div class="ra-dv__fact-k">Co-authors</div>
            <div class="ra-dv__fact-v">{{ profile.stats.coauthors }}</div>
        </div>
        <div class="ra-dv__fact">
            <div class="ra-dv__fact-k">Members they connect</div>
            <div class="ra-dv__fact-v">{{ profile.stats.membersYouConnect }}</div>
        </div>
        <div class="ra-dv__fact">
            <div class="ra-dv__fact-k">ORCID</div>
            <div class="ra-dv__fact-v ra-dv__fact-v--sm">
                @if (profile.orcid) {
                    <a class="ra-pd__orcid" [href]="'https://orcid.org/' + profile.orcid" target="_blank" rel="noopener">{{ profile.orcid }}</a>
                } @else {
                    Not stated
                }
            </div>
        </div>
    </div>

    @if (profile.researchAreas.length > 0) {
        <section class="ra-dv__sec" aria-labelledby="ra-person-detail-areas-h">
            <h2 class="ra-dv__body-h" id="ra-person-detail-areas-h">Research areas</h2>
            <ul class="ra-dv__topics">
                @for (area of profile.researchAreas; track $index) {
                    <li class="ra-dv__topic">{{ area }}</li>
                }
            </ul>
        </section>
    }

    <!-- Server rows carry no id and titles repeat; lists are rebuilt whole per load, so position is the key. -->
    @if (workRows.length > 0) {
        <section class="ra-dv__sec" aria-labelledby="ra-person-detail-works-h">
            <h2 class="ra-dv__body-h" id="ra-person-detail-works-h">Recent works</h2>
            <p class="ra-pd__lede">{{ recentWorksSubtitle }}</p>
            <ul class="ra-pd__works">
                @for (row of workRows; track $index) {
                    <li>
                        @if (row.url) {
                            <a class="ra-pd__work-title" [href]="row.url" target="_blank" rel="noopener">{{ row.title }}</a>
                        } @else {
                            <div class="ra-pd__work-title">{{ row.title }}</div>
                        }
                        @if (row.metaLine) {
                            <div class="ra-pd__work-meta">{{ row.metaLine }}</div>
                        }
                    </li>
                }
            </ul>
        </section>
    }

    @if (HasFundingService) {
        <section class="ra-dv__sec" aria-labelledby="ra-person-detail-funding-h">
            <h2 class="ra-dv__body-h" id="ra-person-detail-funding-h">Funding &amp; service</h2>
            <p class="ra-pd__lede">Public awards and AGU contributions. Award amounts are never shown.</p>
            <dl class="ra-pd__kv">
                @if (profile.funding.length > 0) {
                    <dt>Current awards</dt>
                    <dd>
                        @for (f of profile.funding; track $index) {
                            <div>{{ FundingLine(f) }}</div>
                        }
                    </dd>
                }
                @if (profile.sessionsConvened.length > 0) {
                    <dt>Sessions convened</dt>
                    <dd>
                        @for (s of profile.sessionsConvened; track $index) {
                            <div>{{ SessionLine(s) }}</div>
                        }
                    </dd>
                }
                @if (profile.peerReview) {
                    <dt>Peer review</dt>
                    <dd>{{ PeerReviewLine(profile.peerReview) }}</dd>
                }
                @if (profile.membershipSince) {
                    <dt>AGU membership</dt>
                    <dd>Member since {{ profile.membershipSince }}</dd>
                }
            </dl>
        </section>
    }

    <!-- detail.html:495-503 `What you share`: `.sharenote` + `.sharelist` from the opening card's overlaps. -->
    @if (ShareItems.length > 0) {
        <section class="ra-dv__sec" aria-labelledby="ra-person-detail-share-h">
            <h2 class="ra-dv__body-h" id="ra-person-detail-share-h">What you share</h2>
            <p class="ra-dv__sharenote">Every item below comes from your AGU public profile and ORCID.</p>
            <ul class="ra-dv__sharelist">
                @for (item of ShareItems; track $index) {
                    <li>
                        <span class="ra-dv__share-icon" aria-hidden="true"><i class="fa-solid {{ item.icon }}"></i></span>
                        <div>
                            <div class="ra-dv__share-k">{{ item.title }}</div>
                            <div class="ra-dv__share-v">{{ item.label }}</div>
                        </div>
                    </li>
                }
            </ul>
        </section>
        <!-- The person's Circles, after the share list (P/Member_Hub.html:1629-1644); only for the creative naming this person. -->
        @if (DetailCircles; as ad) { <agu-ad-slot [Placement]="ad" /> }
    }

    <!-- The primary action leads (detail.html:504-506 — the expert record's `.acts`: primary, then Save). -->
    <div class="ra-dv__acts">
        @if (profile.aguProfileUrl) {
            <a class="ra-dv__act ra-dv__act--primary" [href]="profile.aguProfileUrl" target="_blank" rel="noopener"><span class="ra-dv__act-label">AGU profile</span> <span class="ra-dv__act-arrow" aria-hidden="true">↗</span></a>
        } @else {
            <button type="button" class="ra-dv__act ra-dv__act--primary" disabled aria-disabled="true"><span class="ra-dv__act-label">AGU profile</span> <span class="ra-dv__act-arrow" aria-hidden="true">↗</span></button>
        }
        <!-- The old profile view's link rail carried LinkedIn; kept here as a ghost action (review 2026-10-24). ORCID is a fact tile above. -->
        @if (profile.linkedInUrl) {
            <a class="ra-dv__act ra-dv__act--ghost" [href]="profile.linkedInUrl" target="_blank" rel="noopener"><span class="ra-dv__act-label">LinkedIn</span> <span class="ra-dv__act-arrow" aria-hidden="true">↗</span></a>
        }
        <button type="button" class="ra-dv__act ra-dv__act--ghost" [attr.aria-pressed]="itemState.saved" (click)="OnSaveToggle()">
            <agu-icon Name="saved" [Size]="15" />
            <span class="ra-dv__act-label">{{ itemState.saved ? 'Saved' : 'Save' }}</span>
        </button>
        <button type="button" class="ra-dv__act ra-dv__act--ghost" [attr.aria-pressed]="itemState.pinned" (click)="OnPinToggle()">
            <i class="fa-solid fa-thumbtack" aria-hidden="true"></i>
            <span class="ra-dv__act-label">{{ itemState.pinned ? 'Pinned' : 'Pin' }}</span>
        </button>
        <button type="button" class="ra-dv__act ra-dv__act--ghost" (click)="OnDismiss()">
            <i class="fa-solid fa-xmark" aria-hidden="true"></i>
            <span class="ra-dv__act-label">Not for me</span>
        </button>
    </div>
}
